import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
// @mui
import {
    Container,
    Typography,
    Stack,
    Paper,
    Box,
    Alert,
    Divider
} from '@mui/material';
import { ArrowForward, ArrowBack } from '@mui/icons-material';
// components
import FormInput from '../../components/inputs/FormInput';
import OtpInput from '../../components/auth/OtpInput';
import OtpCountdownTimer from '../../components/auth/OtpCountdownTimer';
import OtpResendButton from '../../components/auth/OtpResendButton';
import Btn from '../../components/global/Btn';
import WrapperSection from '../../components/global/WrapperSection';
// hooks & context
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../auth/useAuthContext';
import { usePhoneUpdate } from '../../hooks/auth/usePhoneUpdate';
import { useOtpResend } from '../../hooks/auth/useOtpResend';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { maskPhoneNumber } from '../../utils/otpHelpers';
import { countries } from '../../constants/countres';

export default function PhoneUpdatePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, updateUser } = useAuthContext();

    const [step, setStep] = useState(1); // 1: Enter new phone, 2: Verify OTP
    const [selectedCountryCode, setSelectedCountryCode] = useState(countries[0].phone);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');

    const newPhone = `+${selectedCountryCode}${phoneNumber}`;

    const phoneUpdate = usePhoneUpdate();
    const otpResend = useOtpResend();

    // Step 1: Request OTP for new phone
    const handleRequestOtp = () => {
        if (!phoneNumber) {
            toast.error(t('profile.phone_required'));
            return;
        }

        if (newPhone === user?.phone) {
            toast.error(t('profile.phone_different'));
            return;
        }

        phoneUpdate.requestUpdate(
            { phone: newPhone },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.code_sent_success'));
                    setStep(2);
                    setOtp('');
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.send_failed');
                    toast.error(message);
                }
            }
        );
    };

    // Step 2: Verify OTP and update phone
    const handleVerifyOtp = (otpCode) => {
        phoneUpdate.verifyUpdate(
            { phone: newPhone, otp: otpCode },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.update_success'));

                    // Update user in context
                    if (data?.data && updateUser) {
                        updateUser(data.data);
                    }

                    navigate('/profile');
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.invalid_code');
                    toast.error(message);
                }
            }
        );
    };

    const handleResend = () => {
        otpResend.mutate(
            { channel: 'sms', identifier: newPhone, purpose: 'verify_phone' },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.resend_success'));
                    setOtp('');
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.resend_failed');
                    toast.error(message);
                }
            }
        );
    };

    const handleExpire = useCallback(() => {
        // Only show expired toast if we are not currently resending
        if (!otpResend.isPending) {
            toast.error(t('otp.code_expired'));
        }
    }, [otpResend.isPending, t]);

    return (
        <>
            <Helmet>
                <title> {t('otp.update_phone_title')} | {t('app.name')} </title>
            </Helmet>

            <WrapperSection sx={{ py: 10 }}>
                <Container maxWidth="sm">
                    <Stack spacing={2} sx={{ mb: 5, textAlign: 'center' }}>
                        <Typography variant="h3" sx={{ fontWeight: 700 }}>
                            {t('otp.update_phone_title')}
                        </Typography>
                        <Typography color="text.secondary">
                            {t('otp.update_subtitle')}
                        </Typography>
                    </Stack>

                    <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
                        {step === 1 ? (
                            <Box>
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    {t('otp.current_phone')}: <strong>{user?.phone_number ? maskPhoneNumber(user.phone_number) : t('otp.not_set')}</strong>
                                </Alert>

                                <FormInput
                                    type="tel"
                                    label={t('otp.new_phone')}
                                    placeholder="555-555-555"
                                    selectedCountryCode={selectedCountryCode}
                                    setSelectedCountryCode={setSelectedCountryCode}
                                    phoneNumber={phoneNumber}
                                    setPhoneNumber={setPhoneNumber}
                                    disabled={phoneUpdate.isRequesting}
                                />

                                <Btn
                                    onClick={handleRequestOtp}
                                    width="100%"
                                    widthMob="100%"
                                    sx={{ mt: 3, mx: 'auto' }}
                                    loading={phoneUpdate.isRequesting}
                                    disabled={!phoneUpdate.isRequesting && !phoneNumber}
                                    endIcon={<ArrowForward />}
                                >
                                    {t('otp.send_code')}
                                </Btn>
                            </Box>
                        ) : (
                            <Box>
                                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                                    {t('otp.sent_to')}:<br />
                                    <strong>{maskPhoneNumber(newPhone)}</strong>
                                </Alert>

                                <Box sx={{ mb: 3 }}>
                                    <OtpInput
                                        value={otp}
                                        onChange={setOtp}
                                        onComplete={handleVerifyOtp}
                                        disabled={phoneUpdate.isVerifying}
                                    />
                                </Box>

                                {phoneUpdate.requestData?.data?.expires_at && (
                                    <Box sx={{ mb: 2 }}>
                                        <OtpCountdownTimer
                                            expiresAt={phoneUpdate.requestData.data.expires_at}
                                            onExpire={handleExpire}
                                        />
                                    </Box>
                                )}

                                <Btn
                                    onClick={() => handleVerifyOtp(otp)}
                                    width="100%"
                                    widthMob="100%"
                                    sx={{ mb: 2, mx: 'auto' }}
                                    loading={phoneUpdate.isVerifying}
                                    disabled={!phoneUpdate.isVerifying && otp.length !== 6}
                                >
                                    {t('otp.verify_update')}
                                </Btn>

                                <Divider sx={{ my: 2 }} />

                                <OtpResendButton
                                    onResend={handleResend}
                                    cooldownSeconds={otpResend.cooldownSeconds || phoneUpdate.requestData?.data?.cooldown_seconds}
                                    loading={otpResend.isPending}
                                />

                                <Btn
                                    variant="text"
                                    onClick={() => setStep(1)}
                                    width="100%"
                                    widthMob="100%"
                                    startIcon={<ArrowBack />}
                                    sx={{ mt: 2 }}
                                >
                                    {t('common.previous')}
                                </Btn>
                            </Box>
                        )}
                    </Paper>
                </Container>
            </WrapperSection>
        </>
    );
}
